import { ButtonGroup, Card, Heading, IconButton, LuIcon, toast } from "@overmap-ai/blocks"
import { type Issue, OrganizationAccessLevel, type Stored, submittedAtModelSortFn } from "@overmap-ai/common"
import {
	selectAttachmentsOfIssue,
	selectCommentsOfIssue,
	selectFormSubmissionsOfIssue,
	selectIssueAssociationsOfIssue,
	selectIssueTypeFieldValuesOfIssue,
	selectIssueTypeIdentifierValuesOfIssue,
	selectIssueUpdatesOfIssue,
} from "@overmap-ai/core"
import { UUIDFile, type UUIDPromise } from "@overmap-ai/forms"
import { FileHandler } from "@tiptap/extension-file-handler"
import { Placeholder } from "@tiptap/extensions"
import { EditorContent, useEditor } from "@tiptap/react"
import React, { memo, useCallback, useEffect, useMemo, useState } from "react"

import { ISSUE_ACTIVITY_HISTORY_ITEM_TYPES } from "../../constants"
import { IssueTimelineItemType } from "../../enums/issues"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useShallowEqaulSelector } from "../../store"
import { BASE_EXTENSIONS, LINK_NODES, MENTION_EXTENSIONS } from "../../tiptap"
import { NewLineFloatingMenu, TextBubbleMenu } from "../../tiptap/components"
import { EditorContext } from "../../tiptap/context"
import { uuidFilesFromFiles } from "../../utils/file"
import type { AssetTimelineSize } from "../AssetTimeline/typings"
import { AttachmentButton } from "../Buttons/AttachmentButton"
import { CommentFileThumbnailList } from "../CommentFileThumbnailList/CommentFileThumbnailList"
import { IssueActivityHistoryItemTypeMultiSelectDropdown } from "../DropdownMultiSelects/IssueActivityHistoryItemTypeMultiSelectDropdown"
import { OrganizationAccessRequired } from "../OrganizationAccessRequired/AccessRequired"
import { IssueTimelineAttachmentItem } from "./IssueTimelineAttachmentItem"
import { IssueTimelineCreatedItem } from "./IssueTimelineCreatedItem"
import { IssueTimelineFieldValuesItem } from "./IssueTimelineFieldValuesItem"
import { IssueTimelineFormSubmissionItem } from "./IssueTimelineFormSubmissionItem"
import { IssueTimelineIdentifierItem } from "./IssueTimelineIdentifierItem"
import { IssueTimelineIssueAssociationItem } from "./IssueTimelineIssueAssociationItem"
import { IssueTimelineIssueCommentItem } from "./IssueTimelineIssueCommentItem"
import { IssueTimelineIssueUpdateItem } from "./IssueTimelineIssueUpdateItem"
import type { IssueTimelineItem } from "./typings"

interface IssueActivityHistoryProps {
	issue: Stored<Issue>
	size: AssetTimelineSize
}

export const IssueTimeline = memo((props: IssueActivityHistoryProps) => {
	const { issue, size } = props

	const issueUpdates = useShallowEqaulSelector(selectIssueUpdatesOfIssue(issue.uuid))
	const issueComments = useShallowEqaulSelector(selectCommentsOfIssue(issue.uuid))
	const issueAssociations = useShallowEqaulSelector(selectIssueAssociationsOfIssue(issue.uuid))
	const attachments = useShallowEqaulSelector(selectAttachmentsOfIssue(issue.uuid))
	const issueTypeFieldValues = useShallowEqaulSelector(selectIssueTypeFieldValuesOfIssue(issue.uuid))
	const issueTypeIdentifierValues = useShallowEqaulSelector(selectIssueTypeIdentifierValuesOfIssue(issue.uuid))
	const formSubmissions = useShallowEqaulSelector(selectFormSubmissionsOfIssue(issue.uuid))
	const [visibleItemTypes, setVisibleItemTypes] = useState<IssueTimelineItemType[]>(ISSUE_ACTIVITY_HISTORY_ITEM_TYPES)
	const [files, setFiles] = useState<UUIDFile[]>([])

	const activityHistoryItems = useMemo(() => {
		const items: IssueTimelineItem[] = [
			{
				model: issue,
				type: IssueTimelineItemType.CREATED,
			},
		]

		for (const issueUpdate of issueUpdates) {
			items.push({ model: issueUpdate, type: IssueTimelineItemType.ISSUE_UPDATE })
		}

		for (const issueComment of issueComments) {
			items.push({ model: issueComment, type: IssueTimelineItemType.ISSUE_COMMENT })
		}

		for (const attachment of attachments) {
			items.push({ model: attachment, type: IssueTimelineItemType.ISSUE_ATTACHMENT })
		}

		for (const issueAssociation of issueAssociations) {
			items.push({ model: issueAssociation, type: IssueTimelineItemType.ISSUE_ASSOCIATION })
		}

		for (const fieldValues of issueTypeFieldValues) {
			items.push({ model: fieldValues, type: IssueTimelineItemType.FIELD_VALUES })
		}

		for (const identifierValue of issueTypeIdentifierValues) {
			items.push({ model: identifierValue, type: IssueTimelineItemType.IDENTIFIER_VALUE })
		}

		for (const formSubmission of formSubmissions) {
			items.push({ model: formSubmission, type: IssueTimelineItemType.FORM_SUBMISSION })
		}

		return items.toSorted((a, b) => submittedAtModelSortFn(a.model, b.model)).toReversed()
	}, [
		attachments,
		formSubmissions,
		issue,
		issueAssociations,
		issueComments,
		issueUpdates,
		issueTypeFieldValues,
		issueTypeIdentifierValues,
	])

	const filteredActivityHistoryItems = useMemo(() => {
		const visibleItemTypesSet = new Set(visibleItemTypes)
		return activityHistoryItems.filter(({ type }) => visibleItemTypesSet.has(type))
	}, [activityHistoryItems, visibleItemTypes])

	const editor = useEditor({
		editorProps: {
			attributes: { class: "outline-none" },
		},
		extensions: [
			...BASE_EXTENSIONS,
			...LINK_NODES,
			...MENTION_EXTENSIONS,
			FileHandler.configure({
				onDrop: (_editor, files, _pos) => {
					setFiles((prev) => [...prev, ...uuidFilesFromFiles(files)])
				},
				onPaste: (_editor, files, _htmlContent) => {
					setFiles((prev) => [...prev, ...uuidFilesFromFiles(files)])
				},
			}),
			Placeholder.configure({
				placeholder: "Leave a comment",
			}),
		],
	})

	const handleAttachFiles = useCallback((files: File[]) => {
		setFiles((prev) => [...prev, ...uuidFilesFromFiles(files)])
	}, [])

	const handleDeleteFile = useCallback((file: UUIDFile | UUIDPromise<UUIDFile>) => {
		setFiles((prev) => prev.filter(({ uuid }) => uuid !== file.uuid))
	}, [])

	const resetCommentEditor = useCallback(() => {
		editor.commands.clearContent()
		setFiles([])
	}, [editor])

	const handleCommentSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
		(e) => {
			e.preventDefault()
			e.stopPropagation()

			if (editor.isEmpty && files.length === 0) {
				toast.error({
					title: "Comment required",
					description: "Please add a comment before submitting.",
				})
				return
			}

			const [offlineComment, _promise] = sdk.issueComments.add({
				content: JSON.stringify(editor.getJSON()),
				issue: issue.uuid,
				resolved: false,
			})

			if (files.length > 0) {
				void sdk.issueCommentAttachments.bulkAdd(
					files.map((file) => ({
						file,
						issue_comment: offlineComment.uuid,
					})),
				)
			}

			resetCommentEditor()
		},
		[editor, issue.uuid, resetCommentEditor, files],
	)

	const handleReset = useCallback(() => {
		editor.commands.clearContent()
		setFiles([])
		setVisibleItemTypes(ISSUE_ACTIVITY_HISTORY_ITEM_TYPES)
	}, [editor])

	useEffect(() => {
		handleReset()
	}, [issue.uuid, handleReset])

	return (
		<section className="flex w-full flex-col gap-4">
			<div className="flex items-center justify-between gap-2">
				<Heading size="sm" accentColor="base">
					Timeline
				</Heading>

				<div className="flex items-center">
					<IssueActivityHistoryItemTypeMultiSelectDropdown
						values={visibleItemTypes}
						onValuesChange={setVisibleItemTypes}
						size="sm"
						align="end"
					>
						<IconButton variant="ghost" size={size} accentColor="base">
							<LuIcon icon={modelIconMapping.filter} />
						</IconButton>
					</IssueActivityHistoryItemTypeMultiSelectDropdown>
				</div>
			</div>

			<div className="flex w-full flex-col gap-4">
				{filteredActivityHistoryItems.length > 0
					? filteredActivityHistoryItems.map(({ type, model }) => {
							switch (type) {
								case IssueTimelineItemType.CREATED:
									return <IssueTimelineCreatedItem key={model.uuid} issue={model} size={size} />
								case IssueTimelineItemType.ISSUE_UPDATE:
									return (
										<IssueTimelineIssueUpdateItem
											key={model.uuid}
											issueUpdate={model}
											size={size}
										/>
									)
								case IssueTimelineItemType.ISSUE_COMMENT:
									return (
										<IssueTimelineIssueCommentItem
											key={model.uuid}
											issueComment={model}
											size={size}
										/>
									)
								case IssueTimelineItemType.ISSUE_ATTACHMENT:
									return (
										<IssueTimelineAttachmentItem key={model.uuid} attachment={model} size={size} />
									)
								case IssueTimelineItemType.ISSUE_ASSOCIATION:
									return (
										<IssueTimelineIssueAssociationItem
											key={model.uuid}
											issueAssociation={model}
											size={size}
										/>
									)
								case IssueTimelineItemType.FIELD_VALUES:
									return (
										<IssueTimelineFieldValuesItem
											key={model.uuid}
											issueTypeFieldValues={model}
											size={size}
										/>
									)
								case IssueTimelineItemType.IDENTIFIER_VALUE:
									return (
										<IssueTimelineIdentifierItem
											key={model.uuid}
											issueTypeIdentifierValue={model}
											size={size}
										/>
									)
								case IssueTimelineItemType.FORM_SUBMISSION:
									return (
										<IssueTimelineFormSubmissionItem
											key={model.uuid}
											formSubmission={model}
											size={size}
										/>
									)
							}
						})
					: null}
			</div>

			<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
				<form className="w-full" onSubmit={handleCommentSubmit}>
					<Card className="flex h-max min-h-[80px] w-full flex-col gap-2" size="sm">
						{files.length > 0 ? (
							<CommentFileThumbnailList files={files} onDelete={handleDeleteFile} />
						) : null}

						<EditorContext value={editor}>
							<EditorContent editor={editor} className="h-max w-full grow text-sm" />
							<NewLineFloatingMenu editor={editor} />
							<TextBubbleMenu editor={editor} />
						</EditorContext>

						<ButtonGroup className="flex h-max w-full justify-end gap-2" size={size} accentColor="base">
							<AttachmentButton
								size={size}
								icon
								variant="ghost"
								type="button"
								onAttachFiles={handleAttachFiles}
							>
								<LuIcon icon={modelIconMapping.attachment} />
							</AttachmentButton>
							<IconButton aria-label="submit-comment" type="submit" variant="soft">
								<LuIcon icon={modelIconMapping.arrowUp} />
							</IconButton>
						</ButtonGroup>
					</Card>
				</form>
			</OrganizationAccessRequired>
		</section>
	)
})
IssueTimeline.displayName = "IssueTimeline"
